import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SearchConsole from "../components/search/SearchConsole";
import { Alert } from "../components/ui/Feedback";
import { useSearch } from "../app/SearchProvider";
import { formatDuration } from "../lib/format";
import useLogoSearch from "../hooks/useLogoSearch";

/**
 * Page one: ask the register a question.
 *
 * The console sits on an ink band and overlaps its lower edge, so the search
 * field is unambiguously the thing to use. Everything below is quiet — three
 * plain sentences about what happens next, and a way back into the last
 * search if one is already loaded.
 */
export default function SearchPage() {
  const navigate = useNavigate();
  const { runSearch, status, error, retryNotice, data, lastQuery } =
    useSearch();
  const logo = useLogoSearch();

  async function runAndOpen(payload) {
    try {
      await runSearch(payload);
      navigate("/results");
    } catch {
      // The provider already holds the message; the alert below renders it.
    }
  }

  function handleSearch(payload) {
    logo.clear();
    return runAndOpen(payload);
  }

  // Logo search reads the words out of the image, then runs the same search
  // as a typed name.
  function handleLogoSearch(args) {
    return logo.searchByLogo(args, runAndOpen);
  }

  return (
    <>
      <section className="bg-ink-950 pb-24 pt-14 sm:pb-28 sm:pt-20">
        <div className="mx-auto max-w-[1100px] px-4 sm:px-6 lg:px-8">
          <p className="font-mono text-xs text-[#8f5eeb]">EUIPO · TMview</p>
          <h1 className="mt-3 max-w-[20ch] font-display text-4xl font-medium leading-[1.1] tracking-tight text-white sm:text-5xl">
            Find out who already owns the name you want.
          </h1>
          <p className="mt-4 max-w-reading text-[15px] leading-relaxed text-ink-300">
            Search the European Union register and the national registers behind
            TMview, filter the results to the classes and countries that matter
            to you, and take the whole thing away as a spreadsheet.
          </p>
        </div>
      </section>

      <div className="mx-auto -mt-16 max-w-[1100px] px-4 sm:-mt-20 sm:px-6 lg:px-8">
        <SearchConsole
          onSearch={handleSearch}
          onLogoSearch={handleLogoSearch}
          loading={status === "loading" || logo.reading}
          initialQuery={lastQuery}
        />

        {retryNotice && (
          <div className="mt-4">
            <Alert tone="warning" title="Still working">
              {retryNotice}
            </Alert>
          </div>
        )}

        {logo.notice && (
          <div className="mt-4">
            <Alert tone="info" title="Logo read">
              {logo.notice}
            </Alert>
          </div>
        )}

        {logo.error && (
          <div className="mt-4">
            <Alert tone="error" title="The logo couldn't be searched">
              {logo.error}
            </Alert>
          </div>
        )}

        {status === "error" && error && (
          <div className="mt-4">
            <Alert tone="error" title="The search didn't run">
              {error}
            </Alert>
          </div>
        )}

        {data?.results?.length > 0 && status !== "loading" && (
          <button
            type="button"
            onClick={() => navigate("/results")}
            className="mt-4 flex w-full items-center justify-between rounded border border-ink-200 bg-white px-4 py-3 text-left transition-colors hover:border-ink-300"
          >
            <span className="text-sm text-ink-700">
              Your last search for{" "}
              <span className="font-semibold text-ink-900">
                {data.query?.wordMark}
              </span>{" "}
              returned {data.resultsCount} records
              {data.durationMs ? ` in ${formatDuration(data.durationMs)}` : ""}.
            </span>
            <span className="ml-4 inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-ink-900">
              Open results
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </span>
          </button>
        )}
      </div>

      <section className="mx-auto mt-16 max-w-[1100px] px-4 pb-20 sm:px-6 lg:px-8">
        <div className="grid gap-8 border-t border-ink-200 pt-8 sm:grid-cols-3">
          {[
            {
              heading: "Both registers, one query",
              body: "Searches go to EUIPO first. If those credentials aren't live yet, the same query falls back to TMview automatically and the results tell you which register answered.",
            },
            {
              heading: "Narrow without re-querying",
              body: "Filter the returned rows by name, application number, applicant, jurisdiction, status, class or filing date. Filtering happens in your browser, so it costs nothing and responds instantly.",
            },
            {
              heading: "Leave with a spreadsheet",
              body: "Export selected, filtered or all rows to an .xlsx workbook. It carries the search parameters on a second sheet, so the file still makes sense months later.",
            },
          ].map((item) => (
            <div key={item.heading}>
              <h2 className="font-display text-lg font-semibold text-ink-900">
                {item.heading}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-ink-600">
                {item.body}
              </p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
